import { useState, useCallback, useEffect } from 'react';
import { detectPlatform, getCameraInstructions, PlatformType, CameraInstructions } from '../utils/platform';

export type CameraPermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported';

const DISMISS_KEY = 'camera-perm-dismissed';
const DISMISS_DURATION_MS = 5 * 60 * 1000; // 5 minutes

const useCameraPermission = () => {
    const [platform] = useState<PlatformType>(() => detectPlatform());
    const [instructions] = useState<CameraInstructions>(() => getCameraInstructions(detectPlatform()));

    const checkCamera = useCallback(async (): Promise<CameraPermissionState> => {
        if (!navigator.permissions) return 'unsupported';

        try {
            const result = await navigator.permissions.query({ name: 'camera' as PermissionName });
            return result.state as CameraPermissionState;
        } catch {
            // iOS Safari doesn't support querying 'camera' permission
            return 'unsupported';
        }
    }, []);

    /**
     * Probe camera access by attempting getUserMedia.
     * Immediately stops the stream if successful.
     * Used when permissions API is unsupported (iOS Safari).
     */
    const requestCameraAccess = useCallback(async (): Promise<boolean> => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            stream.getTracks().forEach(t => t.stop());
            return true;
        } catch {
            return false;
        }
    }, []);

    const isDismissedThisSession = useCallback((): boolean => {
        try {
            const ts = sessionStorage.getItem(DISMISS_KEY);
            if (!ts) return false;
            return Date.now() - parseInt(ts, 10) < DISMISS_DURATION_MS;
        } catch {
            return false;
        }
    }, []);

    const markDismissed = useCallback(() => {
        try {
            sessionStorage.setItem(DISMISS_KEY, String(Date.now()));
        } catch {
            // sessionStorage may be unavailable in some contexts
        }
    }, []);

    // Listen for permission state changes
    useEffect(() => {
        let cleanup: (() => void) | undefined;

        const listen = async () => {
            if (!navigator.permissions) return;
            try {
                const status = await navigator.permissions.query({ name: 'camera' as PermissionName });
                const handler = () => {
                    // Permission changed — no local state to update,
                    // but the next checkCamera() call will return the new state
                };
                status.addEventListener('change', handler);
                cleanup = () => status.removeEventListener('change', handler);
            } catch {
                // Permission not queryable
            }
        };

        listen();
        return () => cleanup?.();
    }, []);

    return {
        platform,
        instructions,
        checkCamera,
        requestCameraAccess,
        isDismissedThisSession,
        markDismissed
    };
};

export default useCameraPermission;
